import React, { useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Checkbox,
  CloseButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Stack,
  Text,
  Spacer,
  Button,
} from "@chakra-ui/react";
import { CiFilter } from "react-icons/ci";

// Fixed price ranges
const fixedPriceRanges = [
  { label: "Below 10,000", min: 0, max: 9999 },
  { label: "Between 10k-20k", min: 10000, max: 19999 },
  { label: "Between 20k-30k", min: 20000, max: 29999 },
  { label: "Between 30k-40k", min: 30000, max: 39999 },
  { label: "Between 40k-50k", min: 40000, max: 49999 },
  { label: "50,000 and above", min: 50000, max: Infinity },
];

// Sort options
const sortOptions = [
  "Best Seller",
  "New Arrivals",
  "Recommended",
  "Price High to Low",
  "Price Low to High",
];

export const AccItem = ({ title, data, props }) => {
  const renderContent = () => {
    switch (title) {
      case "Product Details":
        return (
          <Stack spacing={2}>
            <Text><strong>Material:</strong> {data.material?.join(", ")}</Text>
            <Text><strong>Metal Type:</strong> {data.metalType?.join(", ")}</Text>
            <Text><strong>Band Type:</strong> {data.bandType?.join(", ")}</Text>
            <Text><strong>Size Available:</strong> {data.size?.join(", ")}</Text>
            <Text><strong>Resizable:</strong> {data.resizable?.join(", ")}</Text>
            <Text><strong>Made In:</strong> {data.madeIn}</Text>
          </Stack>
        );
      case "Our Diamonds":
        return (
          <Text>
            This product features diamonds totaling{" "}
            <strong>{data.diamondCarats} carats</strong>, offering exceptional
            brilliance and quality assurance.
          </Text>
        );
      case "Free Shipping & Returns":
        return (
          <Text>
            We offer <strong>free shipping and hassle-free returns</strong> on
            all orders. Shop with confidence knowing you can return or exchange
            your item easily.
          </Text>
        );
      case "Craftsmanship & Warranty":
        return (
          <Text>
            Crafted with precision and care, this item includes a{" "}
            <strong>warranty for quality</strong> and authenticity. Each piece
            reflects our commitment to excellence.
          </Text>
        );
      default:
        return <Text>No information available.</Text>;
    }
  };

  return (
    <AccordionItem {...props}>
      <AccordionButton>
        <Text flex={1} textAlign="left" fontWeight="thin">
          {title}
        </Text>
        <AccordionIcon color="#AB7D00" />
      </AccordionButton>
      <AccordionPanel pb={4}>{renderContent()}</AccordionPanel>
    </AccordionItem>
  );
};

export default function FilterMenu({ products, filters, setFilters }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(filters);

  const onOpen = () => {
    setTempFilters(filters);
    setIsOpen(true);
  };

  const onClose = () => setIsOpen(false);

  const handlePriceChange = (min, max) => {
    setTempFilters((prev) => ({
      ...prev,
      priceMin: min,
      priceMax: max,
    }));
  };

  const handleTopCollectionToggle = () => {
    setTempFilters((prev) => ({
      ...prev,
      isTopCollection: !prev.isTopCollection,
    }));
  };

  const handleSortChange = (sortOption) => {
    setTempFilters((prev) => ({
      ...prev,
      sortBy: sortOption,
    }));
  };

  const handleClearAllFilters = () => {
    setTempFilters({});
  };

  return (
    <>
      <HStack cursor="pointer" onClick={onOpen}>
        <Box
          px={3}
          py={1}
          border="1px solid"
          borderColor="blackAlpha.400"
          borderRadius="md"
          display="flex"
          alignItems="center"
        >
          <Text fontSize={18} fontWeight="thin">
            Filter & Sorting
          </Text>
        </Box>
        <CiFilter color="#000" size={24} />
      </HStack>

      <Drawer isOpen={isOpen} onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent bg="#FFFAF2">
          <DrawerHeader>
            <HStack w="full">
              <CloseButton onClick={onClose} />
              <Text fontSize={18} fontWeight="thin">
                Filter & Sorting
              </Text>
              <Spacer />
              <Button
                size="sm"
                variant="outline"
                colorScheme="blackAlpha"
                onClick={handleClearAllFilters}
              >
                Clear All
              </Button>
            </HStack>
          </DrawerHeader>

          <Box px={6} maxH="70vh" overflowY="auto">
            <Accordion allowToggle>
              {/* Sort By */}
              <AccordionItem>
                <AccordionButton>
                  <Text flex={1} textAlign="left" fontWeight="thin">
                    Sort By
                  </Text>
                  <AccordionIcon color="#AB7D00" />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Stack pl={4}>
                    {sortOptions.map((option) => (
                      <HStack key={option} justify="space-between">
                        <Text>{option}</Text>
                        <Checkbox
                          isChecked={tempFilters.sortBy === option}
                          onChange={() => handleSortChange(option)}
                        />
                      </HStack>
                    ))}
                  </Stack>
                </AccordionPanel>
              </AccordionItem>

              {/* Price Filter */}
              <AccordionItem>
                <AccordionButton>
                  <Text flex={1} textAlign="left" fontWeight="thin">
                    Price
                  </Text>
                  <AccordionIcon color="#AB7D00" />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Stack pl={4}>
                    {fixedPriceRanges.map((range, index) => {
                      const isChecked =
                        tempFilters.priceMin === range.min &&
                        tempFilters.priceMax === range.max;
                      return (
                        <HStack key={index} justify="space-between">
                          <Text>{range.label}</Text>
                          <Checkbox
                            isChecked={isChecked}
                            onChange={() =>
                              handlePriceChange(range.min, range.max)
                            }
                          />
                        </HStack>
                      );
                    })}
                  </Stack>
                </AccordionPanel>
              </AccordionItem>

              {/* Top Collection */}
              <AccordionItem>
                <AccordionButton>
                  <Text flex={1} textAlign="left" fontWeight="thin">
                    Top Collection
                  </Text>
                  <AccordionIcon color="#AB7D00" />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <HStack justify="space-between" pl={4}>
                    <Text>Show Only Top Collection</Text>
                    <Checkbox
                      isChecked={tempFilters.isTopCollection === true}
                      onChange={handleTopCollectionToggle}
                    />
                  </HStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>

          <DrawerFooter>
            <Box
              py={3}
              cursor="pointer"
              w="full"
              textAlign="center"
              color="white"
              bg="black"
              onClick={() => {
                setFilters({ ...tempFilters, page: 1 });
                onClose();
              }}
            >
              DISPLAY RESULTS
            </Box>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
